/**
 * `useModels(projectId)` - TanStack Query read hook for the models that
 * belong to a single project.
 *
 * Implements the AC-1408 / AC-1377 P4.2 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4
 * (key conventions), 4 (Phase 4 project resources), and 5.2 (read-hook
 * signatures).
 *
 * ## Why this exists
 *
 * Today, `AgentsPage`, `AgentFormModal`, `ToolManagementSystem`, and
 * `Dashboard` read models through `useProjectStore` (`models`, `loadModels`).
 * That path has no caching, no request deduplication, and forces every
 * mutation to manually re-call `loadModels(projectId)` to refresh consumers.
 * The same stale-modal-data class of bug (AC-1390 / AC-1391 / AC-1394) that
 * the AC-1377 RQ migration closes for workflows applies here; this ticket
 * extends the same pattern to project-scoped models.
 *
 * During Phase 4 the hook MUST coexist with `useProjectStore.models` /
 * `loadModels`. AC-1411 migrates the consumers; AC-1412 deletes the
 * project-store slices once nothing reads them. Until then, the two paths
 * can subscribe in parallel - RQ's per-key request dedup makes the duplicate
 * fetch cheap and the cache slots are independent.
 *
 * ## Behavior
 *
 * - Fetches `apiService.getModels(projectId)` with cache key
 *   `queryKeys.projectModels(projectId)` (the AC-1396 factory; resolves to
 *   `['project', String(projectId), 'models']`). The factory coerces `'7'`
 *   and `7` to the same array shape, so route-string IDs and store-numeric
 *   IDs share a single cache slot. This is the AC-1396 coercion contract.
 *
 * - `enabled` is auto-disabled for empty IDs (`undefined`, `null`, `''`).
 *   Same rationale as `useAgents` / `useWorkflow`.
 *
 * - Caller-provided `options` flow through to `useQuery`. The caller's
 *   `enabled` is AND'ed with the internal id-truthy guard, so passing
 *   `{ enabled: false }` always wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` - those are owned by
 *   the hook. The `Options` type below excludes them.
 *
 * ## Argument forwarded to `apiService.getModels`
 *
 * The `projectId` value is passed through to `apiService.getModels` as a
 * string once the truthy-id guard has cleared. `getModels` URL-interpolates
 * its argument (`/api/v1/projects/${projectId}/models`); we coerce to string
 * to match the apiService signature directly. The cache key factory uses
 * `String(projectId)` independently, so both forms produce the same key.
 *
 * ## Why this hook does NOT read `RegistryEnabledModel`
 *
 * Registry-enabled models live under their own key
 * (`queryKeys.projectRegistries(projectId)`) and are owned by the
 * forthcoming `useRegistries` hook (AC-1409 P4.3). This hook returns the
 * full `Model[]` payload from `GET /api/v1/projects/:projectId/models`.
 * The two queries are separate cache slots; mutations on either side may
 * need to invalidate both - see `useModelMutations.ts` for the cross-key
 * invalidation contract.
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { Model } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook - letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseModelsOptions = Omit<
  UseQueryOptions<Model[], Error, Model[], ReturnType<typeof queryKeys.projectModels>>,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<Model[]>` from TanStack Query.
 *
 * @param projectId Project ID. May be `string`, `number`, `undefined`, or
 *                  `null`. Falsy values disable the query (no fetch fires).
 * @param options   Optional `useQuery` options. `queryKey` and `queryFn` are
 *                  excluded from this type because they are owned by the hook.
 *
 * @example
 *   const { data: models, isLoading } = useModels(projectId);
 *   if (isLoading) return <Spinner />;
 *   return <ModelPicker models={models ?? []} />;
 *
 * @example
 *   // Defer the fetch until a project is selected.
 *   const project = useCurrentProject();
 *   const models  = useModels(project?.project_id);
 */
export function useModels(
  projectId: string | number | undefined | null,
  options?: UseModelsOptions,
): UseQueryResult<Model[], Error> {
  // Treat `undefined`, `null`, and `''` as "no project selected yet".
  const hasId = projectId !== undefined && projectId !== null && projectId !== '';

  // The caller's `enabled` (if provided) is AND'ed with our internal
  // truthy-id guard. Either being falsy keeps the query disabled.
  const callerEnabled = options?.enabled;
  const enabled = hasId && (callerEnabled === undefined ? true : callerEnabled);

  return useQuery<Model[], Error, Model[], ReturnType<typeof queryKeys.projectModels>>({
    // Placeholder ID when `hasId` is false: RQ requires queryKey
    // unconditionally, but `enabled: false` guarantees the queryFn never
    // runs and the placeholder slot is never populated.
    queryKey: queryKeys.projectModels(hasId ? (projectId as string | number) : 0),
    // Forward as string to match the apiService signature. The cache key
    // factory uses `String(...)` independently, so both forms produce the
    // same key.
    queryFn: () => apiService.getModels(String(projectId)),
    ...options,
    enabled,
  });
}

export default useModels;
